import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { WpArticle } from "@/lib/posts.types";
import type { ApprovedTheme } from "@/lib/themes-public";
import { themeByKey } from "@/lib/themes-public";
import type { ThemeKey } from "@/lib/theme-review";
import { resizeImg } from "@/lib/img";
import { displaySlug } from "@/lib/slug-aliases";
import { THEME_DOT, THEME_IMG } from "./theme-ui";

/** One row of the approved archive design (02-articles-archive): image · date + theme · serif title · excerpt · arrow. */
export type ArchiveItem = {
  key: string;
  to: "/yazilar/$slug" | "/cop31/notlar/$slug";
  slug: string;
  date: string;
  theme: ThemeKey | null;
  /** Label shown instead of a theme (e.g. "COP31 notu"). */
  label?: string;
  /** Optional prefix before the theme name, e.g. "Tema: ". */
  themePrefix?: string;
  title: string;
  excerpt: string;
  img: string | null;
};

/** Map posts to archive rows using only approved theme decisions. */
export function postsToArchiveItems(posts: WpArticle[], themes: ApprovedTheme[]): ArchiveItem[] {
  const map = new Map(themes.map((t) => [t.slug, t.primary]));
  return posts.map((p) => {
    const theme = map.get(p.slug) ?? null;
    return {
      key: `p:${p.slug}`,
      to: "/yazilar/$slug",
      slug: displaySlug(p.slug, "tr"),
      date: p.date,
      theme,
      title: p.title,
      excerpt: p.excerpt,
      img: p.img ? resizeImg(p.img, 360, { ar: 2 }) : theme ? THEME_IMG[theme] : null,
    };
  });
}

export function ArchiveRows({ items }: { items: ArchiveItem[] }) {
  return (
    <ul className="divide-y divide-ink/10 border-b border-ink/10">
      {items.map((it) => {
        const t = themeByKey(it.theme);
        return (
          <li key={it.key}>
            <Link
              to={it.to}
              params={{ slug: it.slug }}
              className="group grid grid-cols-[78px_minmax(0,1fr)_auto] items-center gap-x-4 py-3 sm:grid-cols-[150px_minmax(0,1fr)_auto] lg:grid-cols-[176px_minmax(0,1.05fr)_minmax(0,1fr)_auto] lg:gap-x-8 lg:py-2"
            >
              {it.img ? (
                <img src={it.img} alt="" loading="lazy" decoding="async" width={352} height={176} className="h-[62px] w-[78px] object-cover sm:h-[80px] sm:w-[150px] lg:h-[88px] lg:w-[176px]" />
              ) : (
                <span aria-hidden className="block h-[62px] w-[78px] bg-muted sm:h-[80px] sm:w-[150px] lg:h-[88px] lg:w-[176px]" />
              )}
              <span className="min-w-0 py-1 lg:py-3">
                <span className="flex flex-col gap-0.5 text-[12px] text-ink/70 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-2 sm:text-[13px]">
                  <span>{it.date}</span>
                  {(t || it.label) && (
                    <>
                      <span aria-hidden className="hidden sm:inline">·</span>
                      <span className="flex items-center gap-1.5">
                        {t ? <span aria-hidden className={`size-2 rounded-full ${THEME_DOT[t.key]}`} /> : <span aria-hidden className="size-2 rounded-full border border-ink" />}
                        {t ? (it.themePrefix ? `${it.themePrefix}${t.title}` : t.title) : it.label}
                      </span>
                    </>
                  )}
                </span>
                <span className="mt-1 block text-balance font-serif text-[16px] leading-snug text-ink group-hover:text-clay sm:text-[21px] lg:text-[24px]">{it.title}</span>
              </span>
              <span className="hidden text-[14px] leading-relaxed text-ink/70 lg:line-clamp-3">{it.excerpt}</span>
              <ArrowRight aria-hidden className="size-5 shrink-0 text-ink transition-transform group-hover:translate-x-1" />
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
